
"use client";

import Link from 'next/link';
import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { Product, StoreSettings, ProductVariant, DaySchedule, ProductCardProps, User as AppUser } from '@/lib/types';
import { ShoppingCart, X, Zap } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { useCart } from '@/context/CartContext';
import { useExpressCart } from '@/context/ExpressCartContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Label } from '../ui/label';
import { getDay } from 'date-fns';
import { auth, db } from '@/lib/firebase';
import { signOut, onAuthStateChanged, User } from 'firebase/auth';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { useRouter } from 'next/navigation';
import { cn } from '@/lib/utils';
import NoWarrantyAlert from './NoWarrantyAlert';


export function ProductCard({ product, showStock = false }: ProductCardProps) {
  const [showClosedStoreDialog, setShowClosedStoreDialog] = useState(false);
  const [showVariantSelector, setShowVariantSelector] = useState(false);
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
  const [appUser, setAppUser] = useState<AppUser | null>(null);
  const [showNoWarrantyAlert, setShowNoWarrantyAlert] = useState(false);
  const router = useRouter();
  
  const { addToCart: addGlobalCart } = useCart();
  const { addToCart: addExpressCart } = useExpressCart();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
        if (user) {
            const userDocRef = doc(db, 'users', user.uid);
            const unsubUser = onSnapshot(userDocRef, (doc) => {
                if (doc.exists()) {
                    const userData = doc.data() as AppUser;
                    if (userData.isBlocked) {
                        signOut(auth).then(() => {
                            toast({
                                variant: 'destructive',
                                title: 'Acesso Bloqueado',
                                description: 'Sua conta foi bloqueada e você foi desconectado.'
                            });
                            router.push('/404');
                        });
                    } else {
                        setAppUser(userData);
                    }
                } else {
                    signOut(auth);
                    setAppUser(null);
                }
            });
            return () => unsubUser();
        } else {
            setAppUser(null);
        }
    });
    return () => unsubscribe();
  }, [router]);

  const isExpressCard = product.isExpress;
  const onAddToCart = isExpressCard ? addExpressCart : addGlobalCart;

  const variants = product.variants ?? [];
  const hasVariants = variants.length > 0;
  const storeSettings = product.storeSettings;

  const totalStock = useMemo(() => {
    // SÊNIOR: Garantia de valores não-negativos via Math.max
    if (!hasVariants) return Math.max(0, Number(product.stock || 0));
    return variants.reduce((acc, v) => acc + Math.max(0, Number(v.stock || 0)), 0);
  }, [product, variants, hasVariants]);


  const isOutOfStock = totalStock <= 0;
  const isValidUrl = !!product.imageUrl && (product.imageUrl.startsWith('http://') || product.imageUrl.startsWith('https://'));

  const checkStoreOpen = () => {
    if (!storeSettings) return true;
    if (!storeSettings.isOpen) {
        return false; 
    }
    
    const now = new Date();
    const dayOfWeekName = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"][getDay(now)] as DaySchedule['day'];
    
    const todaySchedule = storeSettings.schedule.find(s => s.day === dayOfWeekName);

    if (!todaySchedule || !todaySchedule.isOpen) {
        return storeSettings.allowScheduled || false;
    }

    const currentTime = now.getHours() * 60 + now.getMinutes();
    const [openHour, openMinute] = todaySchedule.openTime.split(':').map(Number);
    const openTime = openHour * 60 + openMinute;
    const [closeHour, closeMinute] = todaySchedule.closeTime.split(':').map(Number);
    const closeTime = closeHour * 60 + closeMinute;

    const isOpenNow = closeTime < openTime 
        ? (currentTime >= openTime || currentTime <= closeTime)
        : (currentTime >= openTime && currentTime <= closeTime);

    return isOpenNow || storeSettings.allowScheduled;
  }

  const handleAddToCartClick = async () => {
    if (isOutOfStock) return;
    
    const user = auth.currentUser;
    if (user) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists() && userDoc.data()?.isBlocked) {
            await signOut(auth);
            toast({
                variant: 'destructive',
                title: "Acesso Negado",
                description: "Sua conta está bloqueada. Por favor, contate o suporte.",
                duration: 9000,
            });
            return;
        }
    }
    
    if (hasVariants) {
      const firstAvailable = variants.find(v => Math.max(0, Number(v.stock || 0)) > 0);
      setSelectedVariantId(firstAvailable?.id || null);
      setShowVariantSelector(true);
      return;
    }

    if (!isExpressCard && !checkStoreOpen()) {
        setShowClosedStoreDialog(true);
        return;
    }
    
    if (product.noWarranty) {
      setShowNoWarrantyAlert(true);
      return;
    }

    onAddToCart(product, 1, undefined, product.availablePartnerId);
  };
  
  const handleVariantAddToCart = () => {
    if (!selectedVariantId) {
        toast({
          variant: 'destructive',
          title: 'Selecione uma opção',
          description: 'Você precisa escolher uma variação para adicionar ao carrinho.'
        });
        return;
    }
    
    const selectedVariant = variants.find(v => v.id === selectedVariantId);
    if(!selectedVariant) return;

    setShowVariantSelector(false);

    if (!isExpressCard && !checkStoreOpen()) {
        setShowClosedStoreDialog(true);
        return;
    }

    if (product.noWarranty) {
      setShowNoWarrantyAlert(true);
      return;
    }

    onAddToCart(product, 1, selectedVariant, selectedVariant.addedFrom);
  }

  const handleBuyNow = () => {
    if (!selectedVariantId) {
      toast({
        variant: 'destructive',
        title: 'Selecione uma opção',
        description: 'Você precisa escolher uma variação para adicionar ao carrinho.'
      });
      return;
    }
    const selectedVariant = variants.find(v => v.id === selectedVariantId);
    if (!selectedVariant) return;

    const action = () => {
        onAddToCart(product, 1, selectedVariant, selectedVariant.addedFrom);
        const destination = isExpressCard ? '/cartexpress' : '/checkoutfast';
        router.push(destination);
    };

    if (product.noWarranty) {
        setShowVariantSelector(false);
        setShowNoWarrantyAlert(true);
    } else {
        setShowVariantSelector(false);
        action();
    }
  }

  const confirmAddToCart = () => {
    setShowClosedStoreDialog(false);
    
    if (hasVariants && selectedVariantId) {
        const selectedVariant = variants.find(v => v.id === selectedVariantId);
        if (selectedVariant) {
             onAddToCart(product, 1, selectedVariant, selectedVariant.addedFrom);
        }
    } else if (!hasVariants) {
        onAddToCart(product, 1, undefined, product.availablePartnerId);
    }
  }

  const handleConfirmNoWarranty = () => {
    setShowNoWarrantyAlert(false);
    if (showVariantSelector) {
      handleVariantAddToCart();
    } else {
      onAddToCart(product, 1, undefined, product.availablePartnerId);
    }
  };

  const getProductDisplayPrice = useCallback(() => {
    const isVipUser = appUser?.isVip || false;
  
    const getPriceFromVariant = (v: ProductVariant) => {
        const hasVipPrice = isVipUser && v.vipPrice && v.vipPrice > 0;
        const price = hasVipPrice ? v.vipPrice! : v.price;
        const originalPrice = hasVipPrice ? v.price : v.originalPrice;
        return { price, originalPrice };
    };
  
    if (!hasVariants) {
        const hasVipPrice = isVipUser && product.vipPrice && product.vipPrice > 0;
        const price = hasVipPrice ? product.vipPrice! : product.price;
        const originalPrice = hasVipPrice ? product.price : product.originalPrice;
        return { price: price, originalPrice, isFrom: false };
    }
  
    const availableVariants = variants.filter(v => Math.max(0, Number(v.stock || 0)) > 0);
    if (availableVariants.length === 0) {
        const firstVariantPrice = getPriceFromVariant(variants[0] || {price: product.price});
        return { price: firstVariantPrice.price, originalPrice: firstVariantPrice.originalPrice, isFrom: false };
    }
    
    const prices = availableVariants.map(v => getPriceFromVariant(v).price);
    const minPrice = Math.min(...prices);
    
    const allSamePrice = availableVariants.every(v => getPriceFromVariant(v).price === minPrice);
  
    if (allSamePrice) {
        const variantData = getPriceFromVariant(availableVariants[0]);
        return { price: variantData.price, originalPrice: variantData.originalPrice, isFrom: false };
    }
  
    return { price: minPrice, originalPrice: undefined, isFrom: true };
  }, [product, variants, hasVariants, appUser]);

  
  const priceInfo = getProductDisplayPrice();
  const hasDiscount = typeof priceInfo.originalPrice === 'number' && Number(priceInfo.originalPrice) > Number(priceInfo.price);
  
  const displayPriceText = (priceInfo.isFrom ? 'A partir de ' : '') + `R$ ${Number(priceInfo.price).toFixed(2)}`;
  const displayOriginalPriceText = priceInfo.originalPrice ? `De R$${priceInfo.originalPrice.toFixed(2)}` : '';

  const ClickableWrapper = ({ children }: { children: React.ReactNode }) => {
    if (isExpressCard && product.variants && product.variants[0]?.addedFrom) {
      return <Link href={`/wolfexpress/${product.id.split('-')[0]}?partner=${product.variants[0].addedFrom}`}>{children}</Link>;
    }
    return <Link href={`/product/${product.slug || product.id}`}>{children}</Link>;
  };


  return (
    <>
    <Card className={cn(
        `w-full max-w-sm flex-shrink-0 overflow-hidden border-2 transition-all duration-300 group bg-card`,
        isOutOfStock ? 'opacity-50' : 'hover:border-primary',
        isExpressCard && 'border-yellow-400'
    )}>
      <CardContent className="p-0">
        <div className="relative">
          <ClickableWrapper>
            <Image
              src={isValidUrl ? product.imageUrl! : 'https://placehold.co/400x400.png'}
              alt={product.name}
              width={400}
              height={400}
              className="object-cover w-full h-64 transition-transform duration-300 group-hover:scale-105"
              data-ai-hint={product['data-ai-hint']}
            />
          </ClickableWrapper>
          <div className="absolute top-2 left-2 flex flex-col gap-1">
            {isOutOfStock && (
                <Badge variant="destructive">ESGOTADO</Badge>
            )}
            {isExpressCard && !isOutOfStock && (
                <Badge className="bg-primary text-primary-foreground flex items-center gap-1 text-xs px-2 py-1 animate-pulse"><Zap className="h-4 w-4"/>Receba Agora!</Badge>
            )}
            {product.displayTags && product.displayTags.map(tag => (
                <Badge 
                  key={tag}
                  className="text-base md:text-lg px-3 md:px-4 py-1 md:py-2 font-bold animate-zoom-in-out"
                  style={{ 
                    backgroundColor: product.displayTagBgColor || '#000000', 
                    color: product.displayTagTextColor || '#FFFFFF' 
                  }}
                >
                  {tag}
                </Badge>
            ))}
          </div>
           {showStock && !isOutOfStock && (
               <Badge variant="secondary" className="absolute top-3 right-3">{totalStock} em estoque</Badge>
           )}
        </div>
        <div className="p-4 space-y-2">
            <ClickableWrapper>
                <h3 className="text-base md:text-lg font-bold font-headline line-clamp-2 h-12">{product.name}</h3>
            </ClickableWrapper>
          <p className={cn("text-sm h-10 mt-4 mb-2 line-clamp-2", isExpressCard ? "text-primary" : "text-muted-foreground")}>{isExpressCard ? "RECEBA C/ EXPRESS EM ATÉ 30Min PARA REGIÃO PRAIA" : product.shortDescription}</p>
          
           <div className="flex flex-wrap items-baseline gap-x-2">
            {hasDiscount ? (
              <>
                <p className="text-xl md:text-2xl font-bold text-primary whitespace-nowrap">
                    {priceInfo.isFrom ? 'A partir de ' : ''}R$ {Number(priceInfo.price).toFixed(2)}
                </p>
                <p className="text-sm text-muted-foreground line-through">
                  {displayOriginalPriceText}
                </p>
              </>
            ) : (
              <p className="text-xl md:text-2xl font-bold text-primary whitespace-nowrap">
                {displayPriceText}
              </p>
            )}
          </div>
          <Button className="w-full mt-2 text-xs sm:text-sm" variant="secondary" onClick={handleAddToCartClick} disabled={isOutOfStock}>
            <ShoppingCart className="mr-2 h-4 w-4" />
            {isOutOfStock ? "Esgotado" : (hasVariants ? "Ver opções" : "Adicionar")}
          </Button>
        </div>
      </CardContent>
    </Card>

    <AlertDialog open={showClosedStoreDialog} onOpenChange={setShowClosedStoreDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Loja Fechada</AlertDialogTitle>
            <AlertDialogDescription>
              Nossa loja está fechada no momento, mas você pode agendar seu pedido para o próximo horário de entrega disponível.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
             <AlertDialogCancel>Cancelar</AlertDialogCancel>
             <AlertDialogAction onClick={confirmAddToCart}>Continuar e Agendar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    {showVariantSelector && (
    <Dialog open={showVariantSelector} onOpenChange={setShowVariantSelector}>
        <DialogContent>
        <DialogHeader>
            <DialogTitle>Selecione uma opção</DialogTitle>
            <DialogDescription>{product.name}</DialogDescription>
        </DialogHeader>
        <div className="relative py-4">
            <div className="max-h-64 overflow-y-auto pr-4">
                <RadioGroup value={selectedVariantId || ''} onValueChange={setSelectedVariantId}>
                    {variants
                    .filter(v => Math.max(0, Number(v.stock || 0)) > 0)
                    .map(variant => (
                        <Label 
                            key={variant.id} 
                            htmlFor={variant.id} 
                            className={`flex justify-between items-center p-3 border rounded-md cursor-pointer has-[:checked]:border-primary mb-2`}
                        >
                            <div>
                                <span>{Object.values(variant.options).join(' / ')}</span>
                                {variant.originalPrice && variant.originalPrice > variant.price ? (
                                    <div className="flex items-baseline gap-2">
                                        <span className="text-xs text-muted-foreground line-through">R${variant.originalPrice.toFixed(2)}</span>
                                        <span className="text-primary font-bold">R${variant.price.toFixed(2)}</span>
                                    </div>
                                ) : (
                                    <span className="text-primary font-bold ml-2">R${variant.price.toFixed(2)}</span>
                                )}
                            </div>
                            <RadioGroupItem value={variant.id} id={variant.id} />
                        </Label>
                    ))}
                </RadioGroup>
            </div>
            <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-card to-transparent pointer-events-none" />
        </div>
        <DialogFooter className="flex-col sm:flex-col sm:space-x-0 gap-2">
            <Button onClick={handleVariantAddToCart} disabled={!selectedVariantId} className="w-full">Adicionar ao Carrinho</Button>
            <Button onClick={handleBuyNow} disabled={!selectedVariantId} className="w-full">Comprar Agora</Button>
            <DialogClose asChild>
                <Button variant="outline" className="w-full">Cancelar</Button>
            </DialogClose>
        </DialogFooter>
        </DialogContent>
    </Dialog>
    )}
    <NoWarrantyAlert 
      isOpen={showNoWarrantyAlert}
      onClose={() => setShowNoWarrantyAlert(false)}
      onConfirm={handleConfirmNoWarranty}
    />
    </>
  );
}
